import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { NamedLink, PrimaryButton, SecondaryButton, ShareModal } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { LOW_LISTING_THRESHOLD } from '../../../constant';
import { parse } from '../../../util/urlHelpers';
import { getNoListingShareContent } from '../../../util/shareContent';

import css from './NoSearchResultsMaybe.module.css';

const NoSearchResultsMaybe = props => {
  const {
    listingsAreLoaded,
    totalItems,
    location,
    resetAll,
    showCreateListingsLink = true,
    onManageDisableScrolling,
    className,
  } = props;

  const config = useConfiguration();
  const intl = useIntl();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const isWithinThreshold = listingsAreLoaded && totalItems <= LOW_LISTING_THRESHOLD;
  if (!isWithinThreshold || isDismissed) {
    return null;
  }

  const hasNoResult = totalItems === 0;
  const hasSearchParams = location.search?.length > 0;
  const searchParams = parse(location.search);
  const categoryOptions = config?.categoryConfiguration?.categories || [];

  const marketplaceRoot = config?.marketplaceRootURL || (typeof window !== 'undefined' ? window.location.origin : '');
  const searchUrl = `${marketplaceRoot}${location.pathname}${location.search}`;

  const shareContent = getNoListingShareContent({
    searchParams,
    categoryOptions,
    searchUrl,
    config,
    intl,
  });

  const closeBoxTitle = intl.formatMessage({
    id: 'NoSearchResultsMaybe.closeBoxTitle',
    defaultMessage: 'Close message',
  });
  const modalHeading = intl.formatMessage({
    id: 'NoSearchResultsMaybe.modalHeading',
    defaultMessage: 'Invite a service provider',
  });

  const createListingLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createListingLink} name="NewListingPage">
      <SecondaryButton as="div" className={css.actionButton}>
        <FormattedMessage id="NoSearchResultsMaybe.createListing" defaultMessage="Create a Listing" />
      </SecondaryButton>
    </NamedLink>
  ) : null;

  return (
    <div className={classNames(css.container, { [css.noResultsContainer]: hasNoResult }, className)}>
      <div className={css.communityBox}>
        <button
          className={css.closeBoxButton}
          onClick={() => setIsDismissed(true)}
          title={closeBoxTitle}
          aria-label={closeBoxTitle}
        >
          ✕
        </button>

        <div className={css.iconHeader}>🤷</div>
        <h3 className={css.heading}>
          <FormattedMessage
            id="NoSearchResultsMaybe.heading"
            defaultMessage="Every thriving neighborhood starts with someone making the first move."
          />
        </h3>
        <p className={css.messageText}>
          <FormattedMessage
            id="NoSearchResultsMaybe.messageText"
            defaultMessage="There isn't much going on in your neighborhood yet — but you can help change that."
          />
        </p>
        <p className={css.messageSubtext}>
          <FormattedMessage
            id="NoSearchResultsMaybe.messageSubtext"
            defaultMessage="Know someone who provides this service? Invite them to join. Or, if you offer a service yourself, create a listing and become one of the first providers in your area."
          />
        </p>

        <div className={css.actionsGroup}>
          <PrimaryButton
            onClick={() => setShareModalOpen(true)}
            className={classNames(css.actionButton, css.inviteButton)}
          >
            <FormattedMessage id="NoSearchResultsMaybe.inviteButton" defaultMessage="Invite a Provider" />
          </PrimaryButton>
          {createListingLinkMaybe}
        </div>

        {hasNoResult && hasSearchParams && resetAll ? (
          <div className={css.resetContainer}>
            <button className={css.resetAllFiltersButton} onClick={e => resetAll(e)}>
              <FormattedMessage id="SearchPage.resetAllFilters" />
            </button>
          </div>
        ) : null}
      </div>

      {shareModalOpen ? (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          title={shareContent.title}
          text={shareContent.text}
          url={shareContent.url}
          heading={modalHeading}
        />
      ) : null}
    </div>
  );
};

NoSearchResultsMaybe.propTypes = {
  listingsAreLoaded: PropTypes.bool,
  totalItems: PropTypes.number,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  resetAll: PropTypes.func,
  showCreateListingsLink: PropTypes.bool,
  onManageDisableScrolling: PropTypes.func,
  className: PropTypes.string,
};

export default NoSearchResultsMaybe;
